import React from 'react';
import ColorPicker from './ColorPicker';
import {
  FINDING_CARD_FIELDS, FINDING_CARD_FIELD_KEYS, FINDING_CARD_SEVERITY_BG,
  FINDING_CARD_EMPTY, FINDING_CARD_FIGURE_PREFIX,
  findingCardMetaRows, findingCardBodySections,
} from './findingCardDefaults';

/**
 * The TSR finding card's settings.
 *
 * Shared by the section panel and the Custom Section component panel so the
 * two forms cannot drift. Everything the renderers read is here.
 *
 * The grid rows and the prose sections are editable LISTS rather than a fixed
 * set of tick boxes, which is the whole difference between this band and the
 * older Findings Detail: there, twelve labels live inside a renderer and the
 * only way to say 'Business Impact' instead of 'Observation' is to edit the
 * code. Here a row is a label and a field, and an author owns both.
 */
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Info'];

function FindingCardsConfig({ config, update }) {
  const inp = {
    padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius)', boxSizing: 'border-box',
    border: '1px solid var(--border)', background: 'var(--bg-input)',
    color: 'var(--text)', fontFamily: 'inherit', minWidth: 0,
  };
  const lbl = { fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
  const row = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
  const group = {
    display: 'flex', flexDirection: 'column', gap: 7, padding: 8,
    borderRadius: 'var(--radius)', border: '1px solid var(--border)',
  };
  const cap = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.5px', color: 'var(--text-secondary)' };
  const note = { ...lbl, fontStyle: 'italic', whiteSpace: 'normal', lineHeight: 1.4 };
  const btn = { background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 11, padding: '1px 6px',
                lineHeight: 1.4, fontFamily: 'inherit' };

  const num = (key, fallback, min, max, width = 56) => (
    <input type="number" min={min} max={max} value={config[key] ?? fallback}
      onChange={e => update(key, Number(e.target.value) || fallback)}
      style={{ ...inp, width }} />
  );
  const colour = (key, fallback, title) => (
    <ColorPicker label={title} size={20} allowNone={false} value={config[key] || fallback}
      onChange={c => update(key, c || fallback)} />
  );
  const check = (key, text, on) => (
    <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
      <input type="checkbox" checked={on} onChange={e => update(key, e.target.checked)}
        style={{ margin: 0, cursor: 'pointer' }} />
      {text}
    </label>
  );

  // Resolved through the same functions the renderers use, so the list on
  // screen is the list that prints -- including a row the author has not
  // touched, which has no entry in config at all.
  const metaRows = findingCardMetaRows(config);
  const bodySections = findingCardBodySections(config);

  /** A reorderable list of {label, field} rows, with one extra flag. */
  const rowList = (key, rows, flagKey, flagText, addField) => {
    const setRows = (next) => update(key, next);
    const setRow = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    const move = (i, delta) => {
      const j = i + delta;
      if (j < 0 || j >= rows.length) return;
      const next = [...rows];
      [next[i], next[j]] = [next[j], next[i]];
      setRows(next);
    };
    return (
      <>
        {rows.map((r, i) => (
          <div key={`${key}-${i}`} style={row}>
            <input value={r.label} placeholder="Label"
              aria-label={`Row ${i + 1} label`}
              onChange={e => setRow(i, { label: e.target.value })}
              style={{ ...inp, width: 150 }} />
            <select value={r.field} aria-label={`Row ${i + 1} field`}
              onChange={e => setRow(i, { field: e.target.value })} style={{ ...inp, width: 170 }}>
              {FINDING_CARD_FIELD_KEYS.map(f => (
                <option key={f} value={f}>{FINDING_CARD_FIELDS[f].label}</option>
              ))}
            </select>
            <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={r[flagKey] === true}
                onChange={e => setRow(i, { [flagKey]: e.target.checked })}
                style={{ margin: 0, cursor: 'pointer' }} />
              {flagText}
            </label>
            <button style={btn} title="Move up" onClick={() => move(i, -1)}>{'↑'}</button>
            <button style={btn} title="Move down" onClick={() => move(i, 1)}>{'↓'}</button>
            <button style={btn} title="Remove this row"
              onClick={() => setRows(rows.filter((_, j) => j !== i))}>{'×'}</button>
          </div>
        ))}
        <div style={row}>
          <button style={btn}
            onClick={() => setRows([...rows, { label: FINDING_CARD_FIELDS[addField].label, field: addField }])}>
            + Add row
          </button>
        </div>
      </>
    );
  };

  // An empty filter means EVERY severity -- that is what both renderers do
  // with one -- so the boxes show all ticked when nothing is stored. The
  // toggle has to work from that same effective list: reading the stored
  // list directly meant unticking one box on an unfiltered band stored
  // ONLY that severity, which is the exact opposite of what was clicked and
  // silently dropped every other finding from the band.
  const sevFilter = Array.isArray(config.severityFilter) ? config.severityFilter : [];
  const shownSeverities = sevFilter.length
    ? SEVERITIES.filter(s => sevFilter.includes(s))
    : [...SEVERITIES];
  const toggleSeverity = (s) => {
    const next = shownSeverities.includes(s)
      ? shownSeverities.filter(x => x !== s)
      : [...shownSeverities, s];
    // Never leave none ticked: the renderers read an empty list as every
    // severity, so storing one would turn the exclusion back into its
    // opposite the moment it was saved.
    if (!next.length) return;
    const ordered = SEVERITIES.filter(x => next.includes(x));
    // All of them ticked is the same as no filter, and storing it as the
    // empty list is what keeps the band including a severity added later.
    update('severityFilter', ordered.length === SEVERITIES.length ? [] : ordered);
  };
  const sevColours = { ...FINDING_CARD_SEVERITY_BG, ...(config.severityColors || {}) };
  const setSevColour = (s, c) =>
    update('severityColors', { ...(config.severityColors || {}), [s]: c || FINDING_CARD_SEVERITY_BG[s] });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <input value={config.heading || ''} placeholder="Heading &mdash; blank for none"
        onChange={e => update('heading', e.target.value)} style={{ ...inp, width: '100%' }} />

      <div style={group}>
        <span style={cap}>Title bar</span>
        <div style={row}>
          <span style={lbl}>Background:</span>{colour('titleBg', '#24416d', 'Title bar background')}
          <span style={lbl}>Text:</span>{colour('titleColor', '#ffffff', 'Title bar text')}
          <span style={lbl}>Size:</span>{num('titleSize', 11, 6, 24)}
          {check('titleBold', 'Bold', config.titleBold !== false)}
        </div>
        <div style={row}>
          {check('showUid', 'Finding ID in the bar', config.showUid !== false)}
          <span style={lbl}>Separator:</span>
          <input value={config.titleSeparator ?? '  |  '}
            onChange={e => update('titleSeparator', e.target.value)} style={{ ...inp, width: 60 }} />
          <span style={lbl}>Badge width:</span>{num('badgeWidth', 1800, 600, 4000, 66)}
          {check('severityUpper', 'Badge in capitals', config.severityUpper !== false)}
        </div>
        <div style={row}>
          <span style={lbl}>Badge text:</span>{colour('severityTextColor', '#ffffff', 'Badge text')}
          {SEVERITIES.map(s => (
            <ColorPicker key={s} label={`${s} badge`} size={20} allowNone={false}
              value={sevColours[s]} onChange={c => setSevColour(s, c)} />
          ))}
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Facts grid</span>
        <span style={note}>
          Two rows share a line unless one is marked wide, which gives it the full width
          &mdash; a CVSS vector or a URL in half a line wraps to three.
        </span>
        {rowList('metaRows', metaRows, 'wide', 'Wide', 'cwe')}
        <div style={row}>
          <span style={lbl}>Label background:</span>{colour('labelBg', '#f2f5f9', 'Label background')}
          <span style={lbl}>Label text:</span>{colour('labelColor', '#24416d', 'Label text')}
          <span style={lbl}>Size:</span>{num('labelSize', 10, 6, 20)}
          {check('labelBold', 'Bold', config.labelBold !== false)}
        </div>
        <div style={row}>
          <span style={lbl}>Value text:</span>{colour('valueColor', '#000000', 'Value text')}
          <span style={lbl}>Size:</span>{num('valueSize', 10, 6, 20)}
          <span style={lbl}>Status:</span>{colour('statusColor', '#c0392b', 'Status colour')}
          <span style={lbl}>Label width:</span>{num('labelWidth', 1700, 600, 4000, 66)}
        </div>
        <div style={row}>
          {check('hideEmpty', 'Leave out rows with nothing in them', config.hideEmpty !== false)}
        </div>
        <div style={row}>
          {check('showBorders', 'Show table grid', config.showBorders === true)}
          <span style={lbl}>Grid colour:</span>{colour('borderColor', '#d8dfe8', 'Grid colour')}
        </div>
        <span style={note}>
          The grid covers the whole card &mdash; the title bar and the severity badge as
          well as the rows beneath them. Off by default: the shaded label column already
          separates the cells, and lines drawn over it read as a spreadsheet.
        </span>
        <span style={note}>
          A row with nothing in it is left out rather than printed empty or as &ldquo;N/A&rdquo;
          &mdash; a card reading &ldquo;CWE: N/A&rdquo; tells a client we looked and found none.
        </span>
      </div>

      <div style={group}>
        <span style={cap}>Prose sections</span>
        <span style={note}>
          Marking a section as evidence numbers its steps and prints the finding&rsquo;s
          attached screenshots underneath, captioned and numbered in one sequence
          through the document.
        </span>
        {rowList('bodySections', bodySections, 'evidence', 'Evidence', 'details')}
        <div style={row}>
          <span style={lbl}>Heading:</span>{colour('sectionLabelColor', '#1f3864', 'Section heading colour')}
          <span style={lbl}>Size:</span>{num('sectionLabelSize', 11, 6, 24)}
          {check('sectionLabelBold', 'Bold', config.sectionLabelBold !== false)}
          <span style={lbl}>Body:</span>{colour('bodyColor', '#000000', 'Body text colour')}
          <span style={lbl}>Size:</span>{num('bodySize', 10, 6, 20)}
        </div>
        <div style={row}>
          {check('sectionsInContents', 'Headings in the table of contents', config.sectionsInContents === true)}
          {check('showEvidence', 'Include screenshots', config.showEvidence !== false)}
          <span style={lbl}>Figure prefix:</span>
          <input value={config.figurePrefix ?? FINDING_CARD_FIGURE_PREFIX}
            placeholder={FINDING_CARD_FIGURE_PREFIX}
            onChange={e => update('figurePrefix', e.target.value)} style={{ ...inp, width: 80 }} />
          <span style={lbl}>Caption:</span>{colour('figureCaptionColor', '#5f6b7a', 'Caption colour')}
          <span style={lbl}>Size:</span>{num('figureCaptionSize', 9, 5, 16)}
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Which findings, and where</span>
        <div style={row}>
          {SEVERITIES.map(s => (
            <label key={s} style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={shownSeverities.includes(s)}
                onChange={() => toggleSeverity(s)} style={{ margin: 0, cursor: 'pointer' }} />
              {s}
            </label>
          ))}
        </div>
        <div style={row}>
          {check('onePerPage', 'Each card on its own page', config.onePerPage === true)}
          <span style={lbl}>Gap between cards:</span>{num('cardGap', 200, 0, 1200, 66)}
          <span style={lbl}>Most cards:</span>
          <input type="number" min={0} max={500} value={config.maxCards ?? 0}
            title="0 means every finding"
            onChange={e => update('maxCards', Math.max(0, Number(e.target.value) || 0))}
            style={{ ...inp, width: 66 }} />
        </div>
      </div>

      <div style={group}>
        <span style={cap}>When there are no findings</span>
        <div style={row}>
          <input value={config.emptyText ?? FINDING_CARD_EMPTY} placeholder={FINDING_CARD_EMPTY}
            aria-label="Empty section sentence"
            onChange={e => update('emptyText', e.target.value)} style={{ ...inp, flex: 1, minWidth: 180 }} />
          <span style={lbl}>Colour:</span>{colour('emptyColor', '#5f6b7a', 'Empty sentence colour')}
        </div>
      </div>
    </div>
  );
}

export default FindingCardsConfig;
